import React, { useEffect, useRef, useState } from "react"
import clsx from "clsx"
import _ from "lodash"
import {
	Grid,
	List,
	ListItem,
	Typography,
	InputAdornment,
	Input,
	Tooltip
} from "@material-ui/core"
import useAutocomplete, { AutocompleteCloseReason } from "@material-ui/lab/useAutocomplete"
import {
	Search as SearchIcon
} from "@material-ui/icons"

import { Divider, AccessibleDrawer, Loading } from "@/components"

import MessageItem from "@/components/ChatMessageBuilder/MessageItem"

import { WABAChannelMessageTemplate } from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"
import useWABATemplateVariableMapping from "@/@integrations/WABA/hooks/useWABATemplateVariableMapping"

import useDidMount from "@/hooks/useDidMount"
import useAsyncState from "@/hooks/useAsyncState"
import useWABACredits from "@/@integrations/WABA/hooks/useWABACredits"

import { useGlobalStateStore } from "@/store/GlobalState"
import { isSmallScreen } from "@/utils/checkDevice"
import { runAfterReactRender } from "@/utils/node"
import { getTemplateCategoryChip, getTemplateStatusChip } from "@/@integrations/WABA/utils/template"
import {
	isValidInteractionChanges
} from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/nativeEvent"

import { INBOX_OPEN_WABA_TEMPLATE_SELECTOR_EVENT } from "@/pages/Attendance/boardNavigationConstants"

import HeaderDrawer from "@/pages/Attendance/Chat/HeaderDrawer"

import useStyles from "@/@integrations/WABA/components/Chat/WABAMessageTemplate/WABAMessageTemplateSelector/styles"
import useChatStyles from "@/pages/Attendance/Chat/styles"
import useSidebarDrawerStyles from "@/pages/Attendance/Chat/ChatListPanel/ChatListHeader/styles"
import useCustomStyles from "@/styles/custom"

import WABAChannelMessageTemplateService from "@/@integrations/WABA/services/WABAChannelMessageTemplate"

import WABAMessageTemplateSelectorSkeleton from "@/@integrations/WABA/skeletons/WABAMessageTemplateSelectorSkeleton"
import HardCoded from "@/services/HardCoded"

export type WABAMessageTemplateSelectorProps = {
	onSelect: (wabaChannelMessageTemplate: WABAChannelMessageTemplate) => Promise<void>
	onClose?: () => void
	inboxChannelId: number
}

const WABAMessageTemplateSelector: React.FC<WABAMessageTemplateSelectorProps> = (props) => {
	const {
		onSelect,
		onClose,
		inboxChannelId
	} = props

	const searchInputRef = useRef<HTMLInputElement>(null)

	const [wabaChannelMessageTemplates, setWABAChannelMessageTemplates] = useState<WABAChannelMessageTemplate[]>([])
	const [focusedWABAChannelMessageTemplateId, setQuickReplyFocusedId] = useState<number | null>(null)
	const [loading, setLoading] = useState(true)
	const [search, setSearch] = useState(null)
	const [opened, setOpened] = useAsyncState(false)

	const { isSidebarCollapsed } = useGlobalStateStore()

	const classes = useStyles()
	const customClasses = useCustomStyles()
	const chatClasses = useChatStyles()
	const sidebarDrawerClasses = useSidebarDrawerStyles({ isCollapsed: isSidebarCollapsed })

	const orderedWabaChannelMessageTemplates = _.orderBy(wabaChannelMessageTemplates, [({ status }) => status === "approved", "name"], ["desc", "asc"])
	const focusedWABAChannelMessageTemplate = wabaChannelMessageTemplates.find(({ id }) => id === focusedWABAChannelMessageTemplateId)

	const canLimitSendWabaTemplateWithCredits = HardCoded.checkFeatureFlag("canUseWABATemplateCreditsLimit")

	const { hasEnoughCreditsToSendTemplate, getCreditsInfo } = useWABACredits()

	const canSelectTemplate = (template: WABAChannelMessageTemplate): boolean => {
		if (!canLimitSendWabaTemplateWithCredits) {
			return true
		}

		return hasEnoughCreditsToSendTemplate(template, 1)
	}

	const isTemplateListItemDisabled = (template: WABAChannelMessageTemplate): boolean => {
		if (template.status !== "approved") {
			return true
		}

		return !canSelectTemplate(template)
	}

	const getTemplateListItemTooltip = (template: WABAChannelMessageTemplate): string => {
		if (template.status !== "approved") {
			return "É possível enviar apenas templates que foram aprovados pela Meta."
		}

		if (!canSelectTemplate(template)) {
			return "Você não possui créditos suficientes para enviar este template."
		}

		return ""
	}

	const handleClose = () => {
		setSearch(null)
		setOpened(() => false)

		onClose?.()
	}

	const { handleSelectTemplate: handleSelectTemplateWithMapping } = useWABATemplateVariableMapping({
		inboxChannelId,
		onSelect,
		onAfterSave: handleClose,
		dialog: {
			requireConfirmationWhenMapped: true,
			title: (name) => `Enviar template "${name}"`,
			saveText: "ENVIAR"
		}
	})

	const loadAllTemplates = async () => {
		const wabaChannelMessageTemplates = await WABAChannelMessageTemplateService.retrieveAllByInboxChannelId(inboxChannelId)

		setWABAChannelMessageTemplates(wabaChannelMessageTemplates)
	}

	const handleOpen = async () => {
		if (!opened.current) {
			setLoading(true)
			setOpened(() => true)

			await getCreditsInfo()
			await loadAllTemplates()

			setLoading(false)
		}
	}

	const isValidTemplate = (wabaChannelMessageTemplate?: WABAChannelMessageTemplate) => {
		return wabaChannelMessageTemplate?.status === "approved" && canSelectTemplate(wabaChannelMessageTemplate)
	}

	const focusOnAutoCompleteInput = () => {
		if (opened.current) {
			runAfterReactRender(() => {
				searchInputRef.current?.focus()
			})
		}
	}

	const onAutoCompleteClose = (reason: AutocompleteCloseReason) => {
		const closedBySelectingItem = reason === "select-option"
		const isTemplateSelectable = isValidTemplate(focusedWABAChannelMessageTemplate)

		const isAbleToSelectTemplate = closedBySelectingItem && isTemplateSelectable

		if (isAbleToSelectTemplate) {
			handleSelectTemplateWithMapping(focusedWABAChannelMessageTemplate as WABAChannelMessageTemplate)
		}
	}

	const onAutoCompleteHighlightChange = (wabaChannelMessageTemplate: WABAChannelMessageTemplate | null) => {
		if (wabaChannelMessageTemplate) {
			setQuickReplyFocusedId(wabaChannelMessageTemplate.id)
		}
	}

	const setupAutoCompleteEventListeners = () => {
		window.addEventListener("keydown", ({ key }) => {
			if (key === "Escape") {
				handleClose()
			}
		}, { capture: true })
	}

	const {
		getRootProps,
		getInputLabelProps,
		getInputProps,
		getListboxProps,
		getOptionProps,
		groupedOptions
	} = useAutocomplete<WABAChannelMessageTemplate>({
		id: "waba-channel-message-template-autocomplete",
		options: orderedWabaChannelMessageTemplates,
		open: true,
		clearOnEscape: true,
		selectOnFocus: true,
		clearOnBlur: true,
		autoHighlight: true,
		value: focusedWABAChannelMessageTemplate,
		getOptionLabel: (option) => option.name,
		getOptionDisabled: (option) => isTemplateListItemDisabled(option),
		onHighlightChange: (_, option) => onAutoCompleteHighlightChange(option),
		onClose: (event, reason) => {
			if (isValidInteractionChanges({
				blackListedElements: {
					htmlTagNames: ["a"]
				},
				event
			})) {
				onAutoCompleteClose(reason)
			}
		},
		blurOnSelect: false
	})

	useEffect(() => {
		focusOnAutoCompleteInput()
	}, [opened.current])

	useDidMount(() => {
		setupAutoCompleteEventListeners()
	})

	useEffect(() => {
		window.addEventListener(INBOX_OPEN_WABA_TEMPLATE_SELECTOR_EVENT, handleOpen)

		return () => {
			window.removeEventListener(INBOX_OPEN_WABA_TEMPLATE_SELECTOR_EVENT, handleOpen)
		}
	})

	return (
		<>
			<AccessibleDrawer
				id="waba-message-template-drawer"
				anchor="left"
				open={opened.current}
				variant="persistent"
				onClose={handleClose}
				onMobileBackButtonPress={handleClose}
				withoutTriggerHistoryBackEvent
				classes={{
					paper: clsx(chatClasses.drawerPaper, !isSmallScreen && sidebarDrawerClasses.drawerPaperOffset)
				}}
			>
				<Grid container>
					<Grid item xs={12}>
						<HeaderDrawer
							title="Templates da WABA"
							onClose={handleClose}
						/>
					</Grid>

					<Grid item xs={12}>
						<Grid container {...getRootProps()}>
							<Grid item xs={12} className={classes.searchInputContainer}>
								<Input
									{...getInputProps()}
									inputRef={searchInputRef}
									value={search}
									startAdornment={
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									}
									fullWidth
									className={classes.searchInput}
								/>
							</Grid>

							<Loading
								loading={loading}
								customLoadingElement={<WABAMessageTemplateSelectorSkeleton />}
							>
								<Grid item xs={12}>
									{groupedOptions.length ? (
										<List
											{...getListboxProps()}
											className={clsx({
												[classes.listContainer]: true,
												[customClasses.scrollBar]: true
											})}
										>
											{groupedOptions.map((wabaChannelMessageTemplate, index) => {
												const isTemplateDisabled = isTemplateListItemDisabled(wabaChannelMessageTemplate)
												const isTemplateFocused = focusedWABAChannelMessageTemplateId === wabaChannelMessageTemplate.id
												const templateTooltip = getTemplateListItemTooltip(wabaChannelMessageTemplate)

												return (
													<ListItem
														{...getOptionProps({ option: wabaChannelMessageTemplate, index })}
														key={wabaChannelMessageTemplate.id}
														className={clsx({
															[classes.listItem]: true,
															[classes.listItemClickable]: !isTemplateDisabled
														})}
														disabled={isTemplateDisabled}
													>
														<Grid
															container
														>
															<Grid
																item
																xs={12}
															>
																<Grid
																	container
																	spacing={1}
																	direction="column"
																>
																	<Grid item>
																		<Typography
																			{...getInputLabelProps()}
																			variant="caption"
																			className={classes.shortcutNameText}
																		>
																			{wabaChannelMessageTemplate.name}
																		</Typography>
																	</Grid>

																	<Grid item>
																		<Grid container spacing={1}>
																			<Grid item>
																				{getTemplateStatusChip(wabaChannelMessageTemplate)}
																			</Grid>

																			<Grid item>
																				{getTemplateCategoryChip(wabaChannelMessageTemplate.category)}
																			</Grid>
																		</Grid>
																	</Grid>

																</Grid>
															</Grid>

															<Divider size={1} orientation="horizontal" />

															<Grid
																item
																xs={12}
															>
																<Tooltip
																	title={templateTooltip}
																	placement="right"
																	disableHoverListener={!templateTooltip}
																>
																	<span
																		className={clsx({
																			[classes.listItemTooltipWrapper]: true,
																			[classes.disabledListItemTooltipWrapper]: isTemplateDisabled
																		})}
																	>
																		<Grid
																			container
																		>
																			<MessageItem
																				copySpecial={false}
																				type={wabaChannelMessageTemplate.message.type}
																				content={wabaChannelMessageTemplate.message.content}
																				mediaName={wabaChannelMessageTemplate.message.mediaName}
																				extraData={wabaChannelMessageTemplate.message.extraData}
																				className={clsx({
																					[classes.unfocusedMessageItem]: !isTemplateFocused || isTemplateDisabled,
																					[classes.messageItemPreview]: true
																				})}
																			/>
																		</Grid>
																	</span>
																</Tooltip>
															</Grid>
														</Grid>
													</ListItem>
												)
											})}
										</List>
									) : (
										<>
											<Divider size={3} orientation="horizontal" />

											<Typography align="center" variant="h2">
												Nenhum template encontrado
											</Typography>
										</>
									)}
								</Grid>
							</Loading>
						</Grid>
					</Grid>
				</Grid>
			</AccessibleDrawer>

			<Grid
				onClick={handleOpen}
			>
				{props.children}
			</Grid>
		</>
	)
}

export default WABAMessageTemplateSelector
